import React, { useEffect, useState } from 'react';
import { CheckCircle2, Fingerprint, Loader } from 'lucide-react';
import PinInput from './PinInput';
import { useAuth } from '../../context/authcontext';
import { OfflineSession } from '../../services/offline/offlineSession';
import { BIOMETRIC_UNAVAILABLE, BiometricError, biometricAvailable, enrollBiometric } from '../../services/offline/biometric';

export interface BiometricSetupProps {
  /** The PIN just chosen at enrollment; otherwise the clinician enters it here */
  pin?: string;
  onDone?: () => void;
}

type State = 'checking' | 'unavailable' | 'ready' | 'busy' | 'done';

/**
 * Optional fingerprint / face unlock (Phase 16D D2, R-308). Offered at enrollment and later from
 * My Day. The PIN always keeps working; where WebAuthn PRF is missing it says so plainly.
 */
const BiometricSetup: React.FC<BiometricSetupProps> = ({ pin: knownPin, onDone }) => {
  const { user } = useAuth();
  const [state, setState] = useState<State>('checking');
  const [pin, setPin] = useState(knownPin || '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    biometricAvailable().then((ok) => { if (!cancelled) setState(ok ? 'ready' : 'unavailable'); }).catch(() => setState('unavailable'));
    return () => { cancelled = true; };
  }, []);

  const setUp = async () => {
    if (!user || pin.length !== 6) return;
    setState('busy');
    setError(null);
    try {
      const label = `${user.firstName || 'pelora'} (offline unlock)`;
      const { credentialId, secret } = await enrollBiometric(`${user.tenantId}:${user.id}`, label);
      await OfflineSession.addBiometric(pin, credentialId, secret);
      setState('done');
      onDone?.();
    } catch (e) {
      if (e instanceof BiometricError && e.code !== 'CANCELLED') setState('unavailable');
      else setState('ready');
      setError(e instanceof Error ? e.message : 'Could not set up fingerprint or face unlock');
    } finally {
      if (!knownPin) setPin('');
    }
  };

  if (state === 'checking') return <p role="status" className="flex items-center gap-2 text-sm text-gray-600"><Loader size={14} className="animate-spin" />Checking this device…</p>;
  if (state === 'unavailable') {
    return <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700" data-testid="biometric-unavailable">Fingerprint or face unlock: {BIOMETRIC_UNAVAILABLE.toLowerCase()}. Your PIN unlocks pelora.</p>;
  }
  if (state === 'done') return <p role="status" className="flex items-center gap-2 text-sm font-medium text-emerald-800"><CheckCircle2 size={16} aria-hidden="true" />Fingerprint or face unlock is on. Your PIN still works.</p>;
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 p-3">
      <p className="text-sm text-gray-700">Unlock with your fingerprint or face instead of typing the PIN. The PIN stays as a backup.</p>
      {!knownPin && <PinInput id="biometric-pin" label="Your offline PIN" value={pin} onChange={setPin} disabled={state === 'busy'} />}
      <button type="button" onClick={setUp} disabled={state === 'busy' || pin.length !== 6} className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg border border-slate-800 text-sm font-semibold text-slate-800 disabled:opacity-50">
        {state === 'busy' ? <Loader size={16} className="animate-spin" /> : <Fingerprint size={18} aria-hidden="true" />}Use fingerprint or face
      </button>
      {error && <p role="alert" className="rounded-lg bg-red-50 p-2 text-sm text-red-800">{error}</p>}
    </div>
  );
};

export default BiometricSetup;
